<template>
  <v-container>
    <p class="step-title">{{ $t('insuranceCardStep.title') }}</p>
    <HfrButton @click="openScanner" type="primary" v-if="isMobile">
      {{ $t('insuranceCardStep.scanButton') }}
    </HfrButton>
    <HfrCard>
      <p v-if="isMobile">{{ $t('insuranceCardStep.orManually') }}</p>
      <v-form ref="insuranceForm" v-model="isInsuranceCardFormValid">
        <v-text-field
          v-model="form.insuranceCardNumber"
          :label="$t('insuranceCardStep.noCard')"
          filled
          class="required"
          :rules="[rules.required, rules.cardNumber]">
        </v-text-field>
        <v-menu
          :close-on-content-click="false"
          ref="menuDate"
          v-model="menuDate"
          :return-value.sync="formattedExpirationDate"
          transition="scale-transition"
          offset-y
          min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedExpirationDate"
              :label="$t('insuranceCardStep.expirationDate')"
              prepend-icon="mdi-calendar"
              readonly
              filled
              v-bind="attrs"
              v-on="on"
              class="required"
              :rules="[rules.required]" />
          </template>
          <v-date-picker
            v-model="form.insuranceCardExpirationDate"
            :active-picker.sync="activeMenuDatePicker"
            :min="minDate"
            locale="fr-CH"
            :first-day-of-week="1"
            @blur="date = doParseDate(dateFormatted)"
            @change="saveDate" />
        </v-menu>
      </v-form>
    </HfrCard>
    <v-alert
      class="mt-2"
      v-model="displayNotification"
      color="red"
      dismissible
      elevation="2"
      type="error">
      {{ notification }}
    </v-alert>
  </v-container>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import HfrButton from '../ui/HfrButton.vue';
import HfrCard from '../ui/HfrCard.vue';
import {
  formatDate,
  parseDate,
  parseInsuranceCardExpirationDate
} from '@/helpers/dates';
import { cardNoRule, requiredRule } from '@/helpers/validationRules';

export default {
  name: 'InsuranceCardStep',
  data() {
    return {
      isInsuranceCardFormValid: null,
      form: {
        insuranceCardNumber: null,
        insuranceCardExpirationDate: null
      },
      rules: {
        required: requiredRule,
        cardNumber: cardNoRule
      },
      displayNotification: false,
      notification: null,
      stepNumber: 2,
      menuDate: false,
      activeMenuDatePicker: null,
      minDate: new Date().toISOString().slice(0,10)
    };
  },
  watch: {
    currentStep: {
      handler(currentStep) {
        if (this.currentStep === this.stepNumber && currentStep) {
          this.autoOpenScanner();
        }
      }
    },
    navigateToNextStep: {
      handler(navigateToNextStep) {
        if (this.currentStep === this.stepNumber && navigateToNextStep) {
          this.validateInsuranceForm();
        }
      }
    },
    isInsuranceCardFormValid(isValid) {
      this.setFormValidity(isValid);
    },
    menuDate(val) {
      val && setTimeout(() => (this.activeMenuDatePicker = 'YEAR'));
    }
  },
  computed: {
    ...mapState('navigation', ['currentStep', 'navigateToNextStep']),
    formattedExpirationDate: {
      get() {
        return formatDate(this.form.insuranceCardExpirationDate);
      },
      set() { }
    },
    isMobile() {
      return (
        window.nativeApp ||
        (window['webkit'] &&
          window['webkit'].messageHandlers &&
          window['webkit'].messageHandlers.nativeApp)
      );
    }
  },
  methods: {
    ...mapActions('insuranceCard', ['sendNumber']),
    ...mapActions('navigation', [
      'doNextStep',
      'setFormValidity',
      'toggleNavigateToNextStep'
    ]),
    openScanner() {
      if (window.nativeApp) {
        // Android
        window.nativeApp.openScanner();
      } else if (
        window['webkit'] &&
        window['webkit'].messageHandlers &&
        window['webkit'].messageHandlers.nativeApp
      ) {
        // IOS
        window['webkit'].messageHandlers.nativeApp.postMessage({
          openScanner: {}
        });
      }
    },
    getAdmissionByCardNumber(number) {
      if (window.nativeApp) {
        // Android
        window.nativeApp.getAdmissionByCardNumber(number);
      } else if (
        window['webkit'] &&
        window['webkit'].messageHandlers &&
        window['webkit'].messageHandlers.nativeApp
      ) {
        // IOS
        window['webkit'].messageHandlers.nativeApp.postMessage({
          getAdmissionByCardNumber: {number: number}
        });
      }
    },
    insuranceCardScanned(number, expirationDate) {
      this.form.insuranceCardNumber = number;
      // convert 31/05/2024 to 2024-05-31
      this.form.insuranceCardExpirationDate =
        parseInsuranceCardExpirationDate(expirationDate);
      this.sendInsuranceCardNumberToWebService();
    },
    sendInsuranceCardNumberToWebService() {
      if (process.env.VUE_APP_PREFILL_FORMS === 'true') return this.doNextStep()

      this.sendNumber({
        insuranceCardNumber: this.form.insuranceCardNumber,
        expirationDate: this.form.insuranceCardExpirationDate
      })
        .then(() => {
          this.getAdmissionByCardNumber(this.form.insuranceCardNumber)
          this.autoOpenScanner = ()=>{}
          this.doNextStep()
        })
        .catch((error) => {
          if (error.response != null) {
            const status = error.response.status;
            if ((status === 400 && error.response.data.expiry_date ) || status === 404) {
              this.notification = this.$t('notifications.cardNo.noResult', [
                this.form.insuranceCardNumber,
                this.form.insuranceCardExpirationDate
              ]);
            } else if ((status === 400 && error.response.data.card_no )) {
              this.notification = this.$t('notifications.cardNo.expired');
            } else if (status === 429) {
              this.notification = this.$t('notifications.cardNo.wait');
            } else if (status === 500) {
              this.notification = this.$t('notifications.cardNo.error');
            } else {
              this.notification = this.$t('notifications.cardNo.noService');
            }
            this.displayNotification = true;
          }
        });
    },
    validateInsuranceForm() {
      this.sendInsuranceCardNumberToWebService();
      this.toggleNavigateToNextStep();
    },
    doParseDate(date) {
      return parseDate(date);
    },
    saveDate(date) {
      this.$refs.menuDate.save(date);
    },
    autoOpenScanner() {
      this.openScanner()
    }
  },
  beforeMount() {
    //Create interface between Webview and Native application
    window['InsuranceForm'] = {
      components: this,
      cardScanned: (number, expirationDate) =>
        this.insuranceCardScanned(number, expirationDate)
    };
  },
  components: {
    HfrButton,
    HfrCard
  }
};
</script>
